"use client";
import styled from 'styled-components';
import BackButton from '../utils/back_button';
import {blog15} from "../../../assets/images";
import { useNavigate } from 'react-router-dom';
import OtherBlogs from '../our_blogs/OtherBlogs';
import CTASection from "../../landing/landing_home/empower"

export default function Blog15() {
 const navigate = useNavigate();
          const handleBackClick = () => {
             window.scrollTo(0, 0);
             navigate("/blog")
          };

  return (
    <>
      <Fullpage>
        <PageBackground>

          <BlogContainer>
          <MainContent>
              <HeaderSection>
                <BackButtonWrapper>
                  <BackButton onClick={handleBackClick} />
                </BackButtonWrapper>
                <BlogHeader
                  title="Don’t Let Missed Forms Cost You: How Smart Forms Saves Students Time and Money"
                  date="October 5 2025"
                  author="POSTED BY CAMPUS INSIGHTS"
                />
              </HeaderSection>

              <HeroImage
                src={blog15}
                alt="Teaching in the Age of Intelligence feature illustration"
              />

            <BlogContent />
          </MainContent>
          </BlogContainer>

        <OtherBlogs currentBlogId={15} />
        </PageBackground>
      </Fullpage>
      
      <CTASection />
    </>
  );
};

const BlogHeader = ({ title, date, author }) => {
  return (
    <HeaderContainer>
      <Title>{title}</Title>
      <MetadataContainer>
        <MetadataWrapper>
          <MetadataGroup>
            <DateText>{date}</DateText>
            <AuthorText>{author}</AuthorText>
          </MetadataGroup>
        </MetadataWrapper>
      </MetadataContainer>
    </HeaderContainer>
  );
};

const BlogContent = () => {
  return (
    <ContentContainer>
      <IntroSection>
        <IntroParagraph>
          College is full of deadlines—financial aid, registration, housing, advising, scholarships. Miss one, and the consequences can be costly. A forgotten form might mean losing thousands in aid, getting dropped from classes, or paying late fees you didn’t expect. For students juggling work, family, and school, it’s easy to fall behind.        </IntroParagraph>
        <IntroParagraph>
          That’s why Campus Insights built AI-FormFill—a smart assistant that helps students complete critical forms faster, easier, and on time. It’s not just a reminder system. It’s a proactive tool that tracks your progress, nudges you when you pause, and even suggests answers based on your secure profile. The goal? Fewer missed steps, fewer missed dollars.
        </IntroParagraph>
      </IntroSection>

      <ContentSection title="Real Financial Impact">
        <ProcessDescription>
            Here’s how AI-FormFill protects your wallet:   
        </ProcessDescription>
        <SubSection>
          <SubHeading>Avoid Late Fees</SubHeading>
          <BodyText>
             Missed registration or housing forms can trigger penalties. AI-FormFill sends timely nudges before deadlines hit.
          </BodyText>
          <SubHeading>Secure Financial Aid</SubHeading>
          <BodyText>
              FAFSA errors or incomplete verification forms can delay thousands in grants and loans. AI-FormFill helps you catch and fix issues early.
          </BodyText>
          <SubHeading>Keep Your Classes</SubHeading>
          <BodyText>
            Incomplete advising or course selection forms can lead to dropped schedules. AI-FormFill keeps you enrolled and on track.
          </BodyText>
          <SubHeading>Prevent Lost Scholarships</SubHeading>
          <BodyText>
            Many scholarships require renewal forms or GPA updates. AI-FormFill ensures you don’t miss out on free money.
          </BodyText>
          <SubHeading>Save Time = Save Money</SubHeading>
          <BodyText>
             Every hour spent fixing paperwork is time you could spend working, studying, or resting. AI-FormFill cuts the hassle
          </BodyText>
        </SubSection>
      </ContentSection>

      <ContentSection title="How It Works">
        <FeatureList>
          <FeatureItem>You start a form—AI-FormFill tracks your progress.</FeatureItem>
          <FeatureItem>If you pause or forget, it sends a friendly nudge.</FeatureItem>
          <FeatureItem>It suggests answers based on your profile (securely).</FeatureItem>
          <FeatureItem>You finish faster, with fewer errors.</FeatureItem>
        </FeatureList>
        <ProcessDescription>
          It’s mobile-friendly, FERPA-compliant, and built for students who don’t have time to chase paperwork.
        </ProcessDescription>
      </ContentSection>

      <ContentSection title="Smart. Simple. Student-Centered.">
        <ContentParagraph>
          AI-FormFill is designed for real life. Whether you’re a first-gen student navigating financial aid for the first time or a returning student trying to register between shifts, this tool helps you stay ahead.
        </ContentParagraph>
        <ContentParagraph>
          Campus Insights built AI-FormFill to reduce stress, save money, and make college more manageable. Because no one should lose aid—or a semester—over a missed form.
        </ContentParagraph>
      </ContentSection>

    </ContentContainer>
  );
};

const ContentSection = ({ title, children }) => {
  return (
    <SectionContainer>
      {title && <SectionTitle>{title}</SectionTitle>}
      <ContentWrapper>{children}</ContentWrapper>
    </SectionContainer>
  );
};

const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const SectionTitle = styled.h2`
  color: #1c1b1f;
  letter-spacing: -0.32px;
  font: 700 32px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 28px;
  }

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  align-self: stretch;
`;

const IntroSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;
  a {
    color: #5e2bff; /* Purple accent */
    text-decoration: underline;
    font-weight: 600;
  }

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const ContentParagraph = styled.p`
  align-self: stretch;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 500 16px/160% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    font-size: 15px;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
`;

const Title = styled.h1`
  width: 1021px;
  color: #1c1b1f;
  letter-spacing: -0.48px;
  font: 700 48px/120% Inter, sans-serif;
  margin: 0;

  @media (max-width: 991px) {
    width: 100%;
    font-size: 36px;
    line-height: 120%;
  }

  @media (max-width: 640px) {
    font-size: 28px;
    line-height: 120%;
  }
`;

const MetadataContainer = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const MetadataWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
`;

const MetadataGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const DateText = styled.time`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 400 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;

const AuthorText = styled.span`
  color: #64636a;
  letter-spacing: 2px;
  text-transform: uppercase;
  font: 500 16px/120% Inter, sans-serif;

  @media (max-width: 991px) {
    font-size: 14px;
  }

  @media (max-width: 640px) {
    font-size: 12px;
    letter-spacing: 1px;
  }
`;


const BlogContainer = styled.article`
  display: flex;
  width: 1120px;
  padding: 56px;
  flex-direction: column;
  align-items: flex-start;
  gap: 64px;
  border-radius: 24px;
  margin: 0 auto;
  box-sizing: border-box;
  background-color: #fff;

  @media (max-width: 991px) {
    width: 100%;
    max-width: 768px;
    padding: 40px 32px;
    gap: 48px;
  }

  @media (max-width: 640px) {
    width: 100%;
    max-width: 100%;
    padding: 24px 16px;
    gap: 32px;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 42px;
  align-self: stretch;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 32px;
  align-self: stretch;
`;

const BackButtonWrapper = styled.div`
  display: flex;
  width: 1008px;
  align-items: center;
  gap: 16px;

  @media (max-width: 991px) {
    width: 100%;
  }
`;

const HeroImage = styled.img`
  width: 1008px;
  height: 400px;
  border-radius: 16px;
  object-fit: cover;

  @media (max-width: 991px) {
    width: 100%;
    height: 300px;
  }

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const PageBackground = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 500px, #F5F5F9 100px, #F5F5F9 100%);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 200px 0;
    flex-direction: column;

  @media (max-width: 640px) {
    padding: 40px 0;
    background: linear-gradient(to bottom, #5e2bff 0px, #5e2bff 100px, #ffffff 100px, #ffffff 100%);
  }
`;

const Fullpage = styled.div`
  height: 100%;
  width: 100%;
//   background-color: #e20707ff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
 

  @media (max-width: 640px) {
    padding: 40px 0;
   
  }
`;

const ProcessDescription = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter,sans-serif;
  margin: 0;

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
  }
`;

const SubSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
`;

const SubHeading = styled.h4`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.2px;
  font: 400 20px/140% Inter, sans-serif;
  margin: 0;
  @media (max-width: 991px) {
    font-size: 18px;
  }
  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const BodyText = styled.p`
  width: 100%;
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  margin: 0;
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FeatureItem = styled.li`
  color: #64636a;
  letter-spacing: -0.08px;
  font: 400 16px/160% Inter, sans-serif;
  list-style-type: none;
  position: relative;
  padding-left: 20px;

  &::before {
    content: '•';
    color: #64636a;
    position: absolute;
    left: 0;
    top: 0;
    font-weight: 700;
    line-height: 160%;
  }

  @media (max-width: 640px) {
    font-size: 14px;
    line-height: 150%;
    /* Adjust ::before for smaller screen if needed */
    &::before {
      line-height: 150%;
    }
  }
`;